// #4625 — the two review-prompt moments that live on the synced settings
// document, and the rule that stops a stale device erasing them.
//
// **Why these two fields cannot use the ordinary settings copy.** The `settings`
// singleton syncs, which is exactly what we want: the once-a-year cap then
// survives a reinstall and follows somebody to a new device. But that document
// is last-writer-wins, and the settings service's own comment about
// `hasLoggedFoodEver` says so plainly — a device that has not synced yet can
// write an older value back over a newer one. For a targeting flag that is
// harmless, because the next event flips it straight back. For a cap it is not:
// the marker saying "we already asked this person" simply disappears, and the
// next qualifying moment asks them again. Nothing reports it, and the only
// visible trace would be somebody being asked to rate the app twice.
//
// So these fields are merged in BOTH directions rather than assigned. The later
// of the two moments always wins, and a blank never replaces a date.
//
// Angular-free on purpose, so the rule can be driven directly by a test — the
// same reason `session-health.ts` and `problem-kind.ts` next door are plain
// modules.
//
// See design-docs/review-prompts.md, "The cap, and where it lives".

import { DateTime } from 'luxon';
import { mergeStoredMoments, readStoredMoment } from '@cf-shared/periodic-suppression';

/**
 * The settings fields holding a review-prompt moment.
 *
 * Both are ISO timestamps written in UTC, stored as `''` when the moment has
 * never happened.
 */
export const REVIEW_PROMPT_TIMESTAMP_SETTING_KEYS: ReadonlyArray<string> = [
  /** When we last asked this person to rate the app. Drives the 365-day cap. */
  'reviewPromptLastAskedAt_utc',
  /** When they last told us something was wrong. Keeps us quiet afterwards. */
  'reviewPromptLastProblemReportedAt_utc',
];

/** Is this one of the fields that merges latest-wins rather than being assigned? */
export function isReviewPromptTimestampSettingKey(key: string): boolean {
  return REVIEW_PROMPT_TIMESTAMP_SETTING_KEYS.indexOf(key) >= 0;
}

/**
 * Read a stored moment, or `undefined` when there is not a usable one.
 *
 * Absent, blank and unreadable all come back the same way. That is deliberate:
 * this value round-trips through a synced JSON document, so a hand-edited or
 * half-written string really can turn up, and Luxon answers an unreadable one
 * with an INVALID DateTime rather than by throwing. Every comparison against an
 * invalid one is false, so letting it through would quietly switch off whichever
 * rule reads it — for the cap, that means asking somebody again and again.
 *
 * The rule itself is `readStoredMoment` in `@cf-shared/periodic-suppression`,
 * shared with everything else in the app that suppresses something for a while.
 * This name stays because `settings.service.ts`, `app.component.ts` and the
 * design doc all use it, and it says which fields it is for.
 */
export function readReviewPromptTimestamp(stored: string | undefined): DateTime | undefined {
  return readStoredMoment(stored);
}

/**
 * Refuse to record a moment we cannot read.
 *
 * **Luxon's `toISO()` answers an invalid `DateTime` with `null` rather than
 * throwing**, and `mergeReviewPromptTimestamp` reads `null` as "nothing
 * usable" — so without this guard, recording a moment with an unusable clock
 * would keep whatever was already stored, save, and resolve successfully having
 * written nothing. For the cap that is exactly the failure this file exists to
 * prevent: the prompt was shown, no marker was written, and the same person can
 * be asked again, with nothing saying so.
 *
 * It throws rather than reporting, because `SettingsService` has no telemetry of
 * its own. Every caller already wraps these writes in a `try`/`catch` that
 * reports, so the throw is what carries the information out. It cannot fire in
 * production — the `now` parameter exists for tests and the default is the
 * system clock — so this guards a programming mistake, not a user's device.
 */
export function assertUsableReviewPromptMoment(when: DateTime, calledFrom: string): void {
  if (!when || !when.isValid) {
    throw new Error(`${calledFrom}: refusing to record an unusable moment `
      + `(${when ? when.invalidReason : 'no value'})`);
  }
}

/**
 * The later of two stored moments — the merge rule itself.
 *
 * Returns the winner's ORIGINAL string rather than a re-serialized one, so a
 * merge that changes nothing really writes nothing back and cannot start a
 * write loop between two devices.
 *
 * An unusable value loses to a usable one, whichever side it is on. It does NOT
 * win by being "different": the point of this function is that a device with
 * nothing stored, or with something broken stored, can never wipe out a real
 * date held by the other side.
 *
 * The rule itself is `mergeStoredMoments` in `@cf-shared/periodic-suppression`,
 * where it sits beside the read so the next person storing a suppression marker
 * on a synced document finds it without knowing about the review prompt.
 */
export function mergeReviewPromptTimestamp(
  local: string | undefined,
  remote: string | undefined,
): string {
  return mergeStoredMoments(local, remote);
}

/**
 * #4816 — the settings field holding EVERY ask, for phase 2's two-a-year cap.
 *
 * A real array, not a JSON string. The two behave oppositely when two devices
 * conflict: the conflict merge keeps the longer array, but takes a string whole
 * from the newer side, which would lose one device's ask.
 *
 * `reviewPromptLastAskedAt_utc` is still written beside it, for ever. A device
 * still running phase 1 reads only that field, and must keep seeing the cap
 * as used. See design-docs/review-prompts.md, "Phase 2".
 */
export const REVIEW_PROMPT_ASK_LIST_SETTING_KEY = 'reviewPromptAskedAt_utc';

/**
 * The union of two stored ask lists, each moment once, oldest first.
 *
 * Both sides are read defensively: the value comes off a synced document and
 * from browser storage, so a damaged one really can appear. A side that is not
 * a list, and an entry that is not a non-blank string, hold no ask and are
 * dropped. **A string that is not a readable date is KEPT**, after the readable
 * ones: it records that an ask happened, and `readReviewPromptAsks` hands it to
 * the gate as an unreadable moment, which counts against the cap. Dropping it
 * would let one extra ask through inside the year.
 *
 * Returns `local` itself when the union adds nothing to it, so a merge that
 * changes nothing writes nothing back and cannot start a write loop between two
 * devices.
 */
export function mergeReviewPromptAskLists(local: unknown, remote: unknown): string[] {
  const byMoment = new Map<number, string>();
  const unreadable = new Set<string>();
  for (const side of [local, remote]) {
    // if a side is not a list at all, it holds nothing usable
    if (!Array.isArray(side)) continue;
    for (const entry of side) {
      // if the entry is not a string with something in it, it is not an ask
      if (typeof entry !== 'string' || entry.trim() === '') continue;
      const moment = readStoredMoment(entry);
      if (moment === undefined) {
        unreadable.add(entry);
      } else if (!byMoment.has(moment.toMillis())) {
        byMoment.set(moment.toMillis(), entry);
      }
    }
  }
  const merged = [
    ...[...byMoment.entries()].sort((a, b) => a[0] - b[0]).map(([, iso]) => iso),
    ...unreadable,
  ];

  const localUnchanged = Array.isArray(local)
    && local.length === merged.length
    && local.every((entry, i) => entry === merged[i]);
  return localUnchanged ? local as string[] : merged;
}

/**
 * Every ask we know of: the list plus the old single-date field, which a phase
 * 1 device may have written without touching the list. Oldest first.
 *
 * An unreadable entry comes back as an INVALID `DateTime`, on purpose: the gate
 * counts an unreadable ask against the cap rather than ignoring it.
 */
export function readReviewPromptAsks(askList: unknown, lastAskedAt: string | undefined): DateTime[] {
  return mergeReviewPromptAskLists(askList, [lastAskedAt])
    .map(iso => DateTime.fromISO(iso));
}
